// No React hooks needed — static page

/**
 * CitadelAnalysisPage
 * 
 * Ad hoc deep-dive analysis based on Rupak Ghose's Substack article:
 * "Citadel Securities and HFT industry diverge"
 * https://rupakghose.substack.com/p/citadel-securities-and-hft-industry
 * 
 * Static data — not connected to Supabase.
 * Styled to match the GPTN dashboard theme.
 */

// ─── Chart data ──────────────────────────────────────────────
const REVENUE_DATA = [
    { year: '2018', citadel: 3.5, virtu: 1.05, optiver: 1.1 },
    { year: '2019', citadel: 3.8, virtu: 0.95, optiver: 1.0 },
    { year: '2020', citadel: 6.7, virtu: 2.0, optiver: 2.8 },
    { year: '2021', citadel: 7.0, virtu: 1.7, optiver: 2.2 },
    { year: '2022', citadel: 7.5, virtu: 1.5, optiver: 3.0 },
    { year: '2023', citadel: 7.9, virtu: 1.3, optiver: 2.5 },
    { year: '2024', citadel: 9.8, virtu: 1.4, optiver: 3.5 },
    { year: '2025', citadel: 13.7, virtu: 1.83, optiver: 4.0 },
  ];
  
  const QUARTERLY_DATA = [
    { q: "Q1'24", rev: 2.8, ni: 1.3 },
    { q: "Q2'24", rev: 2.3, ni: 0.9 },
    { q: "Q3'24", rev: 2.2, ni: 0.85 },
    { q: "Q4'24", rev: 2.4, ni: 1.0 },
    { q: "Q1'25", rev: 3.4, ni: 1.5 },
    { q: "Q2'25", rev: 3.1, ni: 1.1 },
    { q: "Q3'25", rev: 2.65, ni: 1.0 },
    { q: "Q4'25", rev: 3.8, ni: 1.7, highlight: true },
  ];
  
  const REV_PER_HEAD = [
    { firm: 'XTX Markets', value: 12, color: 'rgba(1,23,53,0.7)' },
    { firm: 'HRT', value: 11, color: 'rgba(1,23,53,0.65)' },
    { firm: 'Jane Street', value: 10, color: 'rgba(1,23,53,0.6)' },
    { firm: 'Citadel Sec.', value: 6.8, color: 'rgba(1,23,53,0.5)' },
    { firm: 'Optiver', value: 2.1, color: '#ff3300' },
    { firm: 'Virtu', value: 2.0, color: 'rgba(1,23,53,0.35)' },
    { firm: 'IMC Trading', value: 1.7, color: 'rgba(1,23,53,0.3)' },
  ];
  
  const HEADCOUNT = [
    { firm: 'Jane Street', value: 2800, color: 'rgba(1,23,53,0.7)' },
    { firm: 'Susquehanna', value: 2200, color: 'rgba(1,23,53,0.65)' },
    { firm: 'Citadel Sec.', value: 2000, color: 'rgba(1,23,53,0.6)' },
    { firm: 'Optiver', value: 1900, color: '#ff3300' },
    { firm: 'IMC Trading', value: 1800, color: 'rgba(1,23,53,0.5)' },
    { firm: 'Virtu', value: 950, color: 'rgba(1,23,53,0.4)' },
    { firm: 'Tower Research', value: 800, color: 'rgba(1,23,53,0.35)' },
    { firm: 'Jump Trading', value: 700, color: 'rgba(1,23,53,0.3)' },
    { firm: 'HRT', value: 600, color: 'rgba(1,23,53,0.25)' },
    { firm: 'XTX Markets', value: 280, color: 'rgba(1,23,53,0.2)' },
  ];
  
  const SEGMENT_TABLE = [
    { firm: 'Citadel Securities', color: '#011735', usRetail: { text: '#1 DOMINANT', badge: 'green' }, options: { text: '#1', badge: 'green' }, ficc: { text: '#1 UST BBG', badge: 'green' }, fx: { text: '#2 CME', badge: 'blue' }, credit: { text: '15% blocks', badge: 'blue' }, india: { text: '$450M', badge: 'blue' }, euSI: { text: 'Top 3', badge: 'blue' } },
    { firm: 'Jane Street', color: '#7c3aed', usRetail: { text: '~10% vol', badge: 'blue' }, options: { text: '8% OCC', badge: 'blue' }, ficc: null, fx: null, credit: { text: 'Top 5 BBG', badge: 'green' }, india: { text: '>$450M', badge: 'green' }, euSI: null },
    { firm: 'HRT', color: '#5e7ea0', usRetail: { text: '~20% vol', badge: 'blue' }, options: { text: 'Active' }, ficc: { text: 'Active' }, fx: null, credit: null, india: { text: '~$450M+', badge: 'blue' }, euSI: { text: 'Top tier', badge: 'blue' } },
    { firm: 'Virtu Financial', color: '#008753', usRetail: { text: '#2 retail', badge: 'amber' }, options: { text: 'Mid-tier', badge: 'amber' }, ficc: { text: 'Limited' }, fx: { text: 'Limited' }, credit: null, india: null, euSI: null },
    { firm: 'IMC Trading', color: '#d97706', usRetail: { text: 'Minor' }, options: { text: '#2 retail', badge: 'green' }, ficc: null, fx: null, credit: null, india: null, euSI: null },
    { firm: 'XTX Markets', color: '#ff3300', usRetail: null, options: null, ficc: { text: 'Active' }, fx: { text: '#1 nonbank', badge: 'green' }, credit: null, india: null, euSI: { text: '#1 SI', badge: 'green' } },
    { firm: 'Susquehanna (SIG)', color: '#0d9488', usRetail: { text: 'Minor' }, options: { text: '#3–5 ↓', badge: 'amber' }, ficc: null, fx: null, credit: null, india: null, euSI: null },
    { firm: 'Optiver', color: '#ff3300', highlight: true, usRetail: { text: 'Minor' }, options: { text: 'Major player', badge: 'blue' }, ficc: { text: 'Active' }, fx: { text: 'Active' }, credit: null, india: null, euSI: { text: 'Active SI', badge: 'blue' } },
  ];
  
  const KPI_CARDS = [
    { label: 'Citadel Sec. Q4 Rev', value: '$3.8B', delta: '▲ 60% YoY', deltaType: 'up', sub: 'Optiver est. ~$1B Q4' },
    { label: 'Citadel Sec. Q4 Net Inc', value: '$1.7B', delta: '~45% margin', deltaType: 'up', sub: 'Optiver est. ~30% margin' },
    { label: 'IMC Trading FY Rev', value: '$3.12B', delta: '▲ 40% YoY', deltaType: 'up', sub: 'Peer competitor to Optiver' },
    { label: 'Citadel Notional ADV', value: '$575B', delta: '▲ 40% / 24mo', deltaType: 'up', sub: 'Dwarfs Optiver volumes' },
    { label: 'Optiver Rev / Head', value: '~$2.1M', delta: 'vs Citadel $6.8M', deltaType: 'neutral', sub: '3.2× gap — automation & scale', highlight: true },
  ];
  
  // ─── Simple bar chart component ─────────────────────────────
  function HBar({ data, maxVal, suffix = '', prefix = '' }) {
    const max = maxVal || Math.max(...data.map(d => d.value));
    return (
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {data.map((d, i) => (
          <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:100,fontSize:12,fontWeight:600,color:'var(--text)',textAlign:'right',flexShrink:0}}>{d.firm}</div>
            <div style={{flex:1,height:20,background:'var(--bg)',borderRadius:3,overflow:'hidden',position:'relative'}}>
              <div style={{width:`${(d.value/max)*100}%`,height:'100%',background:d.color||'var(--accent)',borderRadius:3,transition:'width .6s ease'}} />
            </div>
            <div className="mono" style={{fontSize:12,fontWeight:700,color:'var(--text)',minWidth:50,textAlign:'right'}}>{prefix}{d.value}{suffix}</div>
          </div>
        ))}
      </div>
    );
  }
  
  function VBar({ data, labelKey, valueKey, colorFn, maxVal, prefix = '$', suffix = 'B' }) {
    const max = maxVal || Math.max(...data.map(d => d[valueKey]));
    return (
      <div style={{display:'flex',alignItems:'flex-end',gap:6,height:200,padding:'0 4px'}}>
        {data.map((d, i) => {
          const pct = Math.max((d[valueKey] / max) * 100, 3);
          const bg = colorFn ? colorFn(d, i) : 'var(--accent)';
          return (
            <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',height:'100%',justifyContent:'flex-end'}}>
              <div className="mono" style={{fontSize:10,fontWeight:600,color:'var(--text)',marginBottom:4}}>{prefix}{d[valueKey]}{suffix}</div>
              <div style={{width:'70%',height:`${pct}%`,background:bg,borderRadius:'3px 3px 0 0',minHeight:6}} />
              <div style={{fontSize:10,color:'var(--muted)',textAlign:'center',lineHeight:1.2,marginTop:6}}>{d[labelKey]}</div>
            </div>
          );
        })}
      </div>
    );
  }
  
  function GroupedBar({ data, labelKey, series, maxVal }) {
    const max = maxVal || Math.max(...data.flatMap(d => series.map(s => d[s.key] || 0)));
    return (
      <div style={{display:'flex',alignItems:'flex-end',gap:3,height:220,padding:'0 4px'}}>
        {data.map((d, i) => (
          <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',height:'100%',justifyContent:'flex-end'}}>
            <div style={{display:'flex',gap:2,alignItems:'flex-end',width:'100%',justifyContent:'center',flex:1}}>
              {series.map((s, si) => {
                const val = d[s.key] || 0;
                const pct = Math.max((val / max) * 100, 2);
                return (
                  <div key={si} style={{display:'flex',flexDirection:'column',alignItems:'center',width:`${Math.floor(80/series.length)}%`,height:'100%',justifyContent:'flex-end'}}>
                    <div className="mono" style={{fontSize:9,fontWeight:600,color:s.color,marginBottom:2,whiteSpace:'nowrap'}}>{s.prefix||''}{val}{s.suffix||''}</div>
                    <div style={{width:'100%',height:`${pct}%`,background:s.color,borderRadius:'2px 2px 0 0',minHeight:4,opacity:0.85}} />
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:10,color:'var(--muted)',textAlign:'center',lineHeight:1.2,marginTop:6,whiteSpace:'nowrap'}}>{d[labelKey]}</div>
          </div>
        ))}
      </div>
    );
  }
  
  // ─── Badge component ─────────────────────────────────────────
  function Badge({ text, type }) {
    const styles = {
      green: { background: 'rgba(0,135,83,0.08)', color: '#008753', border: '1px solid rgba(0,135,83,0.2)' },
      amber: { background: 'rgba(217,119,6,0.08)', color: '#d97706', border: '1px solid rgba(217,119,6,0.2)' },
      blue: { background: 'rgba(1,23,53,0.06)', color: '#011735', border: '1px solid rgba(1,23,53,0.15)' },
    };
    const s = styles[type] || styles.blue;
    return <span className="mono" style={{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:4,whiteSpace:'nowrap',...s}}>{text}</span>;
  }
  
  // ─── Main page component ─────────────────────────────────────
  export default function CitadelAnalysisPage() {
  
    return (
      <div className="fade-up" style={{maxWidth:1200,margin:'0 auto'}}>
        {/* Header */}
        <div style={{borderBottom:'2px solid var(--border)',paddingBottom:20,marginBottom:24}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
            <div>
              <h1 style={{fontSize:22,fontWeight:700,letterSpacing:'-0.3px',color:'var(--text)'}}>
                HFT Industry Competitive Analysis
              </h1>
              <div style={{fontSize:13,color:'var(--muted)',marginTop:4}}>
                Nonbank Trading Firms — Revenue Divergence, Competitive Positioning & Implications for Optiver
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div className="mono" style={{fontSize:10,fontWeight:700,color:'var(--accent2)',background:'rgba(255,51,0,0.06)',border:'1px solid rgba(255,51,0,0.15)',padding:'4px 12px',borderRadius:4,letterSpacing:'1.2px',textTransform:'uppercase'}}>
                Ad Hoc Analysis
              </div>
              <div className="mono" style={{fontSize:11,color:'var(--muted)',marginTop:6}}>March 2026</div>
            </div>
          </div>
          {/* Source attribution */}
          <div style={{marginTop:14,padding:'10px 14px',background:'rgba(1,23,53,0.03)',borderRadius:8,borderLeft:'3px solid var(--accent)'}}>
            <div style={{fontSize:11,color:'var(--muted)',lineHeight:1.6}}>
              <span style={{fontWeight:700,color:'var(--accent)'}}>Source Analysis:</span>{' '}
              Based on <a href="https://rupakghose.substack.com/p/citadel-securities-and-hft-industry" target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',fontWeight:600,textDecoration:'underline'}}>
                "Citadel Securities and HFT industry diverge"
              </a> by Rupak Ghose (Rupak's Substack, March 2026). 
              Data sourced from Bloomberg, SEC filings, Virtu public filings, IMC annual report, and company disclosures.
            </div>
          </div>
        </div>
  
        {/* Key Takeaways */}
        <div style={{marginBottom:28}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
            <div style={{width:3,height:18,background:'var(--accent2)',borderRadius:2}} />
            <h2 style={{fontSize:16,fontWeight:700}}>Key Takeaways</h2>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            {[
              { icon: '📈', title: 'Citadel has pulled away — and the gap to Optiver is widening', body: 'Citadel Securities revenue grew to $13.7B in 2025 (18% 8-year CAGR). Optiver at an estimated ~$4B is now roughly 3.4× smaller. In 2018 the ratio was ~3.2×. Citadel\'s growth is compounding faster, driven by asset class diversification and retail flow dominance that Optiver lacks.' },
              { icon: '⚔️', title: 'IMC\'s options surge is a direct competitive threat to Optiver', body: 'IMC Trading — Optiver\'s closest peer — rose from #4 to #2 in US retail equity options via the Dash/ION partnership. IMC FY revenue hit $3.12B, approaching Optiver\'s level but with a very different channel mix. As retail options flow migrates off-exchange, Optiver\'s exchange-based options strength may face structural headwinds.' },
              { icon: '⚠️', title: 'The Getco warning is directly relevant to Optiver', body: 'Getco fell from 19% to 4% US equity share in four years by failing to pivot beyond latency arbitrage. Optiver\'s core business — exchange-based options market-making — is analogous. Firms that diversified into mid-frequency (HRT), FICC (Citadel), and client-facing (Jane Street) survived. Those that didn\'t, disappeared.' },
              { icon: '🌏', title: 'Optiver is absent from the India growth story', body: 'Citadel ($450M), Jane Street (>$450M), and HRT (~$450M+) are each generating massive revenue from India. Optiver has no meaningful India presence. This is a significant white space — or a deliberate strategic choice that needs revisiting as competitors scale to $500M+ in Indian markets.' },
              { icon: '🏗️', title: 'FICC is an open lane — but Optiver is not yet capitalising', body: 'Electronic credit trading is at ~50% penetration (vs 10% in 2013). Citadel is now #1 in UST on Bloomberg. Optiver is "Active" in FICC/Rates but not a top-tier player. With bank credit revenues down 26% since 2019, the window for tech-first entrants remains open — but Citadel is pulling further ahead every quarter.' },
              { icon: '📊', title: 'Optiver\'s revenue-per-head ratio signals an automation gap', body: 'At ~$2.1M per employee, Optiver generates 3.2× less than Citadel ($6.8M), 5× less than HRT ($11M), and 5.7× less than XTX ($12M). With ~1,900 staff vs Citadel\'s ~2,000, similar headcount yields vastly different output. This gap reflects automation depth, capital efficiency, and asset class breadth — not effort.' },
            ].map((t, i) => (
              <div key={i} className="card" style={{borderLeft:'3px solid var(--accent)',padding:'14px 16px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                  <span style={{fontSize:18}}>{t.icon}</span>
                  <div style={{fontSize:13,fontWeight:700,color:'var(--text)'}}>{t.title}</div>
                </div>
                <div style={{fontSize:12,color:'var(--muted)',lineHeight:1.6}}>{t.body}</div>
              </div>
            ))}
          </div>
        </div>
  
        {/* KPI Row */}
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
          <div style={{width:3,height:18,background:'var(--accent)',borderRadius:2}} />
          <h2 style={{fontSize:16,fontWeight:700}}>Headline Metrics</h2>
        </div>
        <div style={{fontSize:12,color:'var(--muted)',marginBottom:14,paddingLeft:11}}>Key competitor figures from public filings, media reports, and market data</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:28}}>
          {KPI_CARDS.map((k, i) => (
            <div key={i} style={{
              background: k.highlight ? 'rgba(255,51,0,0.04)' : 'var(--surface)',
              border: `1px solid ${k.highlight ? 'rgba(255,51,0,0.15)' : 'var(--border)'}`,
              borderRadius:8, padding:'14px 16px',
            }}>
              <div className="mono" style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.7px',marginBottom:5}}>{k.label}</div>
              <div className="mono" style={{fontSize:22,fontWeight:700,color:'var(--text)'}}>{k.value}</div>
              <div className="mono" style={{fontSize:11,fontWeight:600,marginTop:3,
                color: k.deltaType === 'up' ? 'var(--green)' : k.deltaType === 'down' ? 'var(--accent2)' : 'var(--muted)'
              }}>{k.delta}</div>
              <div style={{fontSize:10,color:'var(--muted)',marginTop:2}}>{k.sub}</div>
            </div>
          ))}
        </div>
  
        {/* Charts Row 1: Revenue comparison */}
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
          <div style={{width:3,height:18,background:'var(--accent)',borderRadius:2}} />
          <h2 style={{fontSize:16,fontWeight:700}}>Revenue & Performance</h2>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:8}}>
          <div className="card">
            <div className="mono" style={{fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:14}}>
              Citadel Securities vs Virtu vs Optiver — Annual Revenue ($B)
            </div>
            <GroupedBar data={REVENUE_DATA} labelKey="year" maxVal={15}
              series={[
                { key: 'citadel', color: '#011735', prefix: '$', suffix: 'B' },
                { key: 'optiver', color: '#ff3300', prefix: '$', suffix: 'B' },
                { key: 'virtu', color: '#008753', prefix: '$', suffix: 'B' },
              ]} />
            <div style={{display:'flex',gap:16,marginTop:10,fontSize:11,color:'var(--muted)'}}>
              <span><span style={{display:'inline-block',width:10,height:10,background:'#011735',borderRadius:2,marginRight:4}} />Citadel Securities</span>
              <span><span style={{display:'inline-block',width:10,height:10,background:'#ff3300',borderRadius:2,marginRight:4}} />Optiver (est.)</span>
              <span><span style={{display:'inline-block',width:10,height:10,background:'#008753',borderRadius:2,marginRight:4}} />Virtu (Market Making)</span>
            </div>
          </div>
          <div className="card">
            <div className="mono" style={{fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:14}}>
              Citadel Securities — Quarterly Revenue ($B)
            </div>
            <VBar data={QUARTERLY_DATA} labelKey="q" valueKey="rev" prefix="$" suffix="B"
              colorFn={(d) => d.highlight ? 'var(--accent2)' : 'rgba(1,23,53,0.4)'} maxVal={4.5} />
            <div style={{marginTop:12,display:'flex',gap:16,fontSize:11,color:'var(--muted)'}}>
              <span>Q4 2025: <span style={{fontWeight:700,color:'var(--accent2)'}}>$3.8B rev</span>, <span style={{fontWeight:700,color:'var(--text)'}}>$1.7B net income</span> (~45% margin)</span>
            </div>
          </div>
        </div>
        <div style={{fontSize:10,color:'var(--muted)',textAlign:'right',marginBottom:24,fontStyle:'italic'}}>Source: Bloomberg, SEC filings, Virtu public filings</div>
  
        {/* Charts Row 2: Efficiency metrics */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:8}}>
          <div className="card">
            <div className="mono" style={{fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:14}}>
              Revenue per Employee ($M) — 2025 Estimates
            </div>
            <HBar data={REV_PER_HEAD} suffix="M" prefix="$" />
          </div>
          <div className="card">
            <div className="mono" style={{fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:14}}>
              Firm Headcount — Estimated 2025
            </div>
            <HBar data={HEADCOUNT} maxVal={3000} />
          </div>
        </div>
        <div style={{fontSize:10,color:'var(--muted)',textAlign:'right',marginBottom:24,fontStyle:'italic'}}>Source: Company disclosures, LinkedIn, press reports</div>
  
        {/* Competitive Positioning Table */}
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
          <div style={{width:3,height:18,background:'var(--accent)',borderRadius:2}} />
          <h2 style={{fontSize:16,fontWeight:700}}>Competitive Positioning by Segment</h2>
        </div>
        <div style={{fontSize:12,color:'var(--muted)',marginBottom:14,paddingLeft:11}}>Market presence across key asset classes and channels</div>
        <div className="card" style={{padding:0,overflowX:'auto',marginBottom:8}}>
          <table style={{width:'100%',borderCollapse:'separate',borderSpacing:0,fontSize:12}}>
            <thead>
              <tr>
                {['Firm','US Cash Eq (Retail)','Listed Eq Options','FICC / Rates','FX','Corp Bonds','India','EU Equities SI'].map((h,i) => (
                  <th key={i} style={{textAlign:'left',fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.5px',padding:'11px 13px',borderBottom:'2px solid var(--border)',background:'var(--bg)',whiteSpace:'nowrap'}}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SEGMENT_TABLE.map((row, ri) => (
                <tr key={ri} style={{background: row.highlight ? 'rgba(232,93,26,0.04)' : ri % 2 === 0 ? 'var(--bg)' : 'rgba(0,0,0,0.01)'}}>
                  <td style={{padding:'10px 13px',borderBottom:`1px solid ${row.highlight ? 'rgba(232,93,26,0.15)' : 'var(--border)'}`,fontWeight:row.highlight ? 700 : 600,color:'var(--text)',whiteSpace:'nowrap'}}>
                    <span style={{display:'inline-block',width:8,height:8,borderRadius:99,background:row.color,marginRight:8}} />
                    {row.firm}
                  </td>
                  {['usRetail','options','ficc','fx','credit','india','euSI'].map((col, ci) => (
                    <td key={ci} style={{padding:'10px 13px',borderBottom:'1px solid var(--border)',color:'var(--muted)'}}>
                      {row[col] ? (
                        row[col].badge ? <Badge text={row[col].text} type={row[col].badge} /> : <span className="mono" style={{fontSize:11}}>{row[col].text}</span>
                      ) : <span className="mono" style={{fontSize:11,color:'var(--border)'}}>—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{fontSize:10,color:'var(--muted)',textAlign:'right',marginBottom:24,fontStyle:'italic'}}>Source: SEC filings, Virtu public data, Global Trading PFOF aggregation</div>
  
        {/* Strategic Implications for Optiver */}
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
          <div style={{width:3,height:18,background:'var(--accent2)',borderRadius:2}} />
          <h2 style={{fontSize:16,fontWeight:700}}>Strategic Implications for Optiver</h2>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12}}>
          {[
            { label: '1 — Options Moat Under Pressure', body: 'Optiver\'s core listed options franchise faces a two-front threat: IMC is capturing retail options wholesale flow via Dash/ION, while Citadel and Jane Street dominate both retail and institutional. Exchange-based flow — Optiver\'s strength — may face structural migration off-exchange as retail wholesaling grows.' },
            { label: '2 — FICC & Credit: Move or Miss', body: 'Optiver is "Active" in FICC but not a top-tier player. Citadel took a decade to reach #1 in UST — the compounding advantage of early entry is real. Electronic credit is at 50% penetration with bank revenues falling. If Optiver doesn\'t meaningfully scale FICC in the next 2–3 years, the window may close.' },
            { label: '3 — India: Strategic Blind Spot', body: 'Three competitors now earn $400M+ each from India. Optiver has no material presence. India is the fastest-growing APAC market for prop firms. Every year Optiver delays, incumbents compound their data, regulatory, and talent advantages. This is arguably the single largest geographic gap in Optiver\'s strategy.' },
          ].map((s, i) => (
            <div key={i} style={{background:'var(--bg)',borderLeft:'3px solid var(--accent2)',borderRadius:'0 8px 8px 0',padding:'14px 16px',border:'1px solid var(--border)',borderLeftColor:'var(--accent2)'}}>
              <div className="mono" style={{fontSize:10,fontWeight:800,color:'var(--accent2)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:6}}>{s.label}</div>
              <div style={{fontSize:12,color:'var(--muted)',lineHeight:1.6}}>{s.body}</div>
            </div>
          ))}
        </div>
        {/* Second row of implications */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:28}}>
          {[
            { label: '4 — Revenue-per-Head: The Automation Imperative', body: 'At $2.1M rev/head, Optiver is in the bottom tier alongside Virtu ($2.0M) and IMC ($1.7M). HRT ($11M) and XTX ($12M) achieve 5× more with 3× fewer people. This isn\'t a headcount problem — it\'s an automation, capital deployment, and asset class breadth problem. Closing this gap requires structural change, not incremental hiring.' },
            { label: '5 — The Getco Precedent: Diversify or Decline', body: 'Getco was once the dominant HFT player. It failed to pivot beyond latency arbitrage and collapsed from 19% to 4% US equity share in four years. Optiver\'s heavy reliance on exchange-based options market-making carries the same concentration risk. HRT\'s successful pivot to mid-frequency and Citadel\'s multi-asset expansion are the survival templates.' },
          ].map((s, i) => (
            <div key={i} style={{background:'var(--bg)',borderLeft:'3px solid var(--accent2)',borderRadius:'0 8px 8px 0',padding:'14px 16px',border:'1px solid var(--border)',borderLeftColor:'var(--accent2)'}}>
              <div className="mono" style={{fontSize:10,fontWeight:800,color:'var(--accent2)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:6}}>{s.label}</div>
              <div style={{fontSize:12,color:'var(--muted)',lineHeight:1.6}}>{s.body}</div>
            </div>
          ))}
        </div>
  
        {/* India Growth */}
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
          <div style={{width:3,height:18,background:'var(--accent)',borderRadius:2}} />
          <h2 style={{fontSize:16,fontWeight:700}}>Citadel Securities India — Revenue Trajectory</h2>
        </div>
        <div className="card" style={{marginBottom:8}}>
          <div style={{display:'flex',alignItems:'flex-end',gap:24,justifyContent:'space-around',padding:'20px 0'}}>
            {[
              { year: 'FY22 (launch)', value: 75 },
              { year: 'FY23', value: 170 },
              { year: 'FY24', value: 325 },
              { year: 'FY25', value: 450 },
            ].map((d, i) => (
              <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                <div className="mono" style={{fontSize:14,fontWeight:700,color:'var(--accent)'}}>${d.value}M</div>
                <div style={{width:60,height:`${(d.value/450)*140}px`,background:'linear-gradient(180deg, var(--accent), rgba(1,23,53,0.3))',borderRadius:'4px 4px 0 0',minHeight:8}} />
                <div style={{fontSize:11,color:'var(--muted)',textAlign:'center'}}>{d.year}</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:11,color:'var(--muted)',textAlign:'center',borderTop:'1px solid var(--border)',paddingTop:8}}>
            6× revenue growth in 4 years — India is now a $450M+ revenue centre for Citadel Securities
          </div>
          <div style={{marginTop:12,padding:'10px 14px',background:'rgba(232,93,26,0.04)',borderRadius:6,borderLeft:'3px solid #ff3300'}}>
            <div style={{fontSize:11,color:'var(--muted)',lineHeight:1.6}}>
              <span style={{fontWeight:700,color:'#ff3300'}}>Optiver context:</span>{' '}
              Optiver has no material India revenue today. Citadel, Jane Street, and HRT each earn $400M+ from India. 
              If Optiver entered in FY26 and matched Citadel's early trajectory, it could reach ~$150–200M by FY29 — but 
              competitors will have compounded a 4+ year head start in data, regulatory relationships, and local talent.
            </div>
          </div>
        </div>
        <div style={{fontSize:10,color:'var(--muted)',textAlign:'right',marginBottom:24,fontStyle:'italic'}}>Source: Bloomberg News, company filings</div>
  
        {/* Footer attribution */}
        <div style={{borderTop:'2px solid var(--border)',paddingTop:16,marginTop:12}}>
          <div style={{fontSize:11,color:'var(--muted)',lineHeight:1.6}}>
            <strong style={{color:'var(--text)'}}>About this analysis:</strong> This is an ad hoc GPTN competitive intelligence report based on Rupak Ghose's article{' '}
            <a href="https://rupakghose.substack.com/p/citadel-securities-and-hft-industry" target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',textDecoration:'underline'}}>
              "Citadel Securities and HFT industry diverge"
            </a>{' '}
            (March 2026). All financial data sourced from public filings, Bloomberg, and media reports. This dashboard is for informational purposes only and does not constitute investment advice.
          </div>
        </div>
      </div>
    );
  }